import Header from "@/components/Header";
import Footer from "@/components/Footer";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen">
      <Header />

      <main className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl font-bold mb-8">Privacy Policy</h1>
            <p className="text-muted-foreground mb-8">
              <strong>Last Updated:</strong> January 2025
            </p>

            <div className="prose prose-lg max-w-none space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-4">Introduction</h2>
                <p className="text-muted-foreground">
                  Daniel Albert Law Firm ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website danielalbertlaw.com or use our services.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Information We Collect</h2>
                <h3 className="text-xl font-semibold mb-3">Personal Information</h3>
                <p className="text-muted-foreground mb-4">
                  We may collect personal information that you provide directly to us, including but not limited to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Name and contact information (email address, phone number, mailing address)</li>
                  <li>Details about your legal matter when you request a consultation</li>
                  <li>Information you provide in forms, emails, or phone calls</li>
                  <li>Payment information for legal services (processed securely by third-party payment processors)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">Automatically Collected Information</h3>
                <p className="text-muted-foreground mb-4">
                  When you visit our website, we may automatically collect certain information, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages viewed and time spent on pages</li>
                  <li>Referring website addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">How We Use Your Information</h2>
                <p className="text-muted-foreground mb-4">We use the information we collect to:</p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Provide legal services and respond to your inquiries</li>
                  <li>Communicate with you about your case or consultation request</li>
                  <li>Process payments and maintain financial records</li>
                  <li>Improve our website and user experience</li>
                  <li>Send periodic emails about legal updates or firm news (with your consent)</li>
                  <li>Comply with legal obligations and professional responsibilities</li>
                  <li>Protect against fraud and unauthorized activities</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Information Sharing and Disclosure</h2>
                <p className="text-muted-foreground mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                  <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our website or conducting our business (subject to confidentiality agreements)</li>
                  <li><strong>Professional Consultants:</strong> With expert witnesses, co-counsel, or other professionals as necessary for your legal representation</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Attorney-Client Privilege</h2>
                <p className="text-muted-foreground">
                  Once an attorney-client relationship is established, communications between you and our firm are protected by attorney-client privilege. However, <strong>submitting a contact form or requesting a consultation does not automatically create an attorney-client relationship.</strong> Do not include confidential information in initial contact forms.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Data Security</h2>
                <p className="text-muted-foreground">
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Cookies and Tracking Technologies</h2>
                <p className="text-muted-foreground">
                  Our website uses cookies and similar technologies to enhance user experience, analyze website traffic, and understand user behavior. You can control cookies through your browser settings, though disabling cookies may affect website functionality.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Third-Party Links</h2>
                <p className="text-muted-foreground">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Children's Privacy</h2>
                <p className="text-muted-foreground">
                  Our website and services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Your Rights</h2>
                <p className="text-muted-foreground mb-4">You have the right to:</p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information (subject to legal and professional obligations)</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Object to certain data processing activities</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Changes to This Privacy Policy</h2>
                <p className="text-muted-foreground">
                  We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on our website with an updated "Last Updated" date. Continued use of our website after changes indicates acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
                <p className="text-muted-foreground mb-4">
                  If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-card p-6 rounded-lg">
                  <p className="font-semibold mb-2">Daniel Albert Law Firm</p>
                  <p className="text-muted-foreground">10925 Beechnut St A106</p>
                  <p className="text-muted-foreground">Houston, TX 77072</p>
                  <p className="text-muted-foreground mt-2">
                    <strong>Phone:</strong> (832) 930-3059 (English) | (832) 356-7254 (Español)
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Email:</strong> info@danielalbertlaw.com
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
